import React, { useEffect, useRef, useState } from 'react';
import { List, Message } from '@meitu/saas-ui';
import type { Editor } from '@meitu/saas-editor-core';
import { WATERMARK_TYPE, type WatermarkLayer } from '@meitu/saas-editor-core/es/layers/watermark-layer';
import { imageConverter } from '@meitu/saas-editor-core/es/utils/file-kit';
import IconFont from '@meitu/xdesign-icon-font';
import { MaterialCard } from '@/components/material-card';
import { LOCAL_WATERMARK_CONFIRM, useWatermarkConfirm } from '@/components/watermark-confirm';
import { WheelCard } from '@/components/wheel-card';
import { useLocaleStore } from '@/i18n';
import { MaterialTopicItem, WatermarkListItem } from '@/types';
import { formatImageUrl } from '@/utils/file-kit';

export interface IAddWatermarkProps {
    materialId: string;
    zipUrl: string;
    mode: number;
}

const MAX_PAGE_COUNT = 6;
const funName = 'sy';

type AddWatermarkProps = {
    editor: Editor;
    data: IAddWatermarkProps | null;
    setData: Function;
};

export const AddWatermark: React.FC<AddWatermarkProps> = React.memo(({ data, setData, editor }) => {
    const stageSize = editor.services.frame.getActiveFrameSize();

    const { $t } = useLocaleStore();

    useEffect(() => {
        const fn = async () => {
            editor.services.mask.show({
                loading: true,
                background: true,
            });
            await editor
                .getInstance<WatermarkLayer>('WatermarkLayer')
                .addWatermarkLayer(
                    WATERMARK_TYPE.TEXT,
                    stageSize,
                    '',
                    { materialId: data!.materialId, zipUrl: data!.zipUrl },
                    data?.mode === 2,
                );
            editor.services.mask.hide();
            Message.success($t((v) => v.collapse.watermarkCollapse.addSuccessText));
            setData(null);
        };
        data && fn();
    }, [data]);
    return null;
});

interface IWatermarkListCollapse {
    editor: Editor;
    setShowDetailsCollapse: Function;
    isShow?: boolean;
    recordId?: number;
    style?: React.CSSProperties;
    showSvipCorner: boolean;
    fetchMaterialTopicList: (data: any) => Promise<any>;
    fetchSaveRecentUsed: (data: any) => Promise<any>;
    setRecentWatermarkUsedList: (data: MaterialTopicItem) => void;
    recentWatermarkUsedList?: MaterialTopicItem;

    // @FIXME 类型改成 IUserInfoParams
    userInfo: any;
    useListTrack: Function;
}

const AllWatermarkList = (props: IWatermarkListCollapse) => {
    const { $t } = useLocaleStore();
    const { setShowDetailsCollapse, isShow = true, style, useListTrack } = props;
    const [materialList, setMaterialList] = useState<MaterialTopicItem[]>([]);

    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isInit, setIsInit] = useState<boolean>(false);
    const [cursor, setCursor] = useState<string>('');
    const [addData, setAddData] = useState<IAddWatermarkProps | null>(null);
    const startTime = useRef(new Date().getTime());
    const renderListed = useRef(false);

    const layers = props.editor.services.frame.getLayers(props.editor.services.frame.getActiveFrameId());

    const { watermarkConfirm } = useWatermarkConfirm();

    const funcID = '18_18';

    /**
     * 请求专题列表
     */
    const getMaterialTopicList = async () => {
        if (!hasMore || isLoading) {
            return;
        }
        setIsLoading(true);
        const {
            data: {
                data: { cursor: newCursor = '', data },
            },
        } = await props.fetchMaterialTopicList({
            material_count: MAX_PAGE_COUNT,
            count: 10,
            code: 'watermark_category',
            cursor,
        });
        newCursor ? setCursor(newCursor) : setHasMore(false);
        data && setMaterialList((v) => v.concat(data));
        setIsLoading(false);
    };
    /**
     * 请求所有数据
     */
    const getAllData = async () => {
        try {
            setIsInit(true);
            await getMaterialTopicList();
        } catch (error) {
            console.log('请求素材失败');
        } finally {
            setIsInit(false);
        }
    };
    // 统计列表渲染时间
    useEffect(() => {
        if (!materialList.length || renderListed.current) return;
        const duration = new Date().getTime() - startTime.current;
        window._stat_track?.('render_durations', {
            duration,
            type: 'list',
            func: '14',
        });
        renderListed.current = true;
    }, [materialList]);

    useEffect(() => {
        getAllData();
    }, []);

    const handleClick = (name: string, id: number | string) => {
        setShowDetailsCollapse(true, name, id);
    };

    const handleSelectMaterial = async (item: WatermarkListItem, labelId: string | number) => {
        const fn = async () => {
            // editor.services.mask.show({
            //     loading: true,
            //     background: true,
            // });
            // 防止修改到展示的缩略图
            setAddData({
                materialId: String(item.id),
                zipUrl: item.zip_file.url,
                mode: item.picture_display_mode,
            });
            // loading.hide();
            // addImgToLayers(image);
            if (props.userInfo) {
                props.fetchSaveRecentUsed({
                    material_id: +item.id,
                    code: 'watermark',
                    module: 1,
                });
                const data: MaterialTopicItem = props.recentWatermarkUsedList || {
                    id: 'recentUsed',
                    materials: [],
                    name: '常用素材',
                    cursor: '',
                };

                let materials: WatermarkListItem[] = (data.materials as any[]) || [];
                // 过滤数据
                materials = materials.filter((v) => +v.id !== +item.id);

                // 添加数据
                if (materials.length) materials.unshift(item);

                // 截取前六个
                data.materials = materials;
                props.setRecentWatermarkUsedList({ ...data });
            }
            window._stat_track?.('tpbj_subfunction_click', {
                func: funcID,
                open: 1,
                record_id: props.recordId,
                click_type: 1,
            });
            window._stat_track?.('tpbj_subfunction_tick', {
                func: funcID,
                record_id: props.recordId,
            });
            window._stat_track?.('tpbj_material_click', {
                func: funName,
                material_id: item.id,
                label_id: labelId,
                local_ind: 0,
                type_id: item.type_id,
                belong: 0,
                is_search: 1,
                material_vip: item.threshold,
                manage_type: 0,
            });
            window._stat_track?.('tpbj_material_tick', {
                func: funName,
                material_id: item.id,
                label_id: labelId,
                type_id: item.type_id,
                belong: 0,
                local_ind: 0,
                is_search: 1,
                material_vip: item.threshold,
                manage_type: 0,
            });
        };

        const watermarkLayer = props.editor.getInstance<WatermarkLayer>('WatermarkLayer').getWatermarkLayer();
        const local = localStorage.getItem(LOCAL_WATERMARK_CONFIRM);
        if (watermarkLayer && !local) {
            watermarkConfirm(
                () => {
                    fn();
                },
                () => {},
            );
            return;
        } else {
            fn();
        }
    };

    let trackAllData: WatermarkListItem[] = [];
    materialList.forEach((item, index) => {
        trackAllData = trackAllData.concat(item.materials as WatermarkListItem[]);
    });
    // 素材曝光埋点
    useListTrack('waterMarkCollapseList', trackAllData, (data: any) => {
        let { id, category, vip, type } = data;
        if (isNaN(parseInt(category))) {
            category = '';
        }
        window._stat_track?.('tpbj_material_show', {
            func: 'sy',
            material_id: id,
            label_id: category,
            type_id: type,
            belong: 0,
            manage_type: 0,
            is_search: 1,
            material_vip: vip,
            local_ind: 0,
        });
    });

    if (!isShow) return null;
    return (
        <div className="all-material-list" style={style}>
            <AddWatermark data={addData} setData={setAddData} editor={props.editor} />
            <List<MaterialTopicItem>
                className="watermark-list"
                id="waterMarkCollapseList"
                loading={isLoading}
                hasMore={hasMore}
                dataSource={isInit ? [] : materialList}
                nextPageRequest={getMaterialTopicList}
                renderItem={(data) => (
                    // <ListCard
                    //     key={data.id}
                    //     title={data.name}
                    //     rightText={data.materials.length >= 6 ? '查看更多' : ''}
                    //     onClick={() => handleClick(data.name || '', data.id)}
                    // >
                    <WheelCard
                        cardWidth={78}
                        cardMargin={6}
                        cardLength={data.materials.length}
                        key={data.id}
                        title={data.name}
                        rightText={
                            data.materials.length >= 6 ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {$t((v) => v.collapse.watermarkCollapse.all)}
                                    <IconFont name="ChevronRightBlack" fontSize={12}></IconFont>
                                </div>
                            ) : (
                                ''
                            )
                        }
                        onClick={() => handleClick(data.name || '', data.id)}
                    >
                        {data.materials.map((material, materialIndex) => {
                            return (
                                <div key={materialIndex}>
                                    <MaterialCard
                                        onClick={() => handleSelectMaterial(material as WatermarkListItem, data.id)}
                                        width={78}
                                        key={materialIndex}
                                        src={imageConverter(
                                            formatImageUrl((material as WatermarkListItem).thumbnail.url),
                                            320,
                                        )}
                                        cornerSrc={
                                            props.showSvipCorner ? (material as WatermarkListItem).corner_mark?.url : ''
                                        }
                                        cornerPlacement="topRight"
                                        cardStyle={{
                                            marginRight: 6,
                                            height: 78,
                                            backgroundColor: 'var(--background-system-space-holder)',
                                        }}
                                        trackInfo={{
                                            'data-track-category': data.id,
                                            'data-track-id': material.id,
                                            'data-track-vip': material.threshold,
                                            'data-track-type': material.type_id,
                                        }}
                                    ></MaterialCard>
                                </div>
                            );
                        })}
                        {/* </ListCard> */}
                    </WheelCard>
                )}
            />
        </div>
    );
};
export default React.memo(AllWatermarkList);
